import { useEffect, useMemo, useState } from 'react';

import { useAnalyticsFacade } from 'src/modules/AnalyticsFacade';
import { parseCookies } from 'src/modules/safe-cookies';
import {
  experimentsAfterAuthIntialised,
  genAIExperimentControlKey,
  FEATURE_EXPERIMENTS_AFTER_AUTH,
  genAIExperimentInExperimentKey,
  genAIOverallCohort,
} from 'src/store/experiments';
import { mapToSeekExperiments } from 'src/store/experiments/experimentHelpers';
import type {
  TExperimentKeys,
  TSeekExperiments,
} from 'src/store/experiments/types';
import { updateFeatureFlagsAfterAuth } from 'src/store/featureFlags';
import { useDispatch, useSelector } from 'src/store/react';
import { selectExperiments, selectSeekerId } from 'src/store/selectors';
import { useMelwaysZone } from 'src/utils/melwaysHelper';

import { logger } from '../../modules/logger';

const experimentsContainsKey = (
  experiments: TSeekExperiments,
  key: string,
): boolean =>
  Object.entries(experiments).filter(
    ([experimentKey]): boolean => experimentKey === key,
  ).length > 0;

export const useInitialiseSignedInExperiments = () => {
  const dispatch = useDispatch();

  const [isExperimentsInitialised, setIsExperimentsInitialised] =
    useState(false);

  const cookies = useMemo(() => parseCookies(), []);
  const zone = useMelwaysZone();
  const seekerId = useSelector(selectSeekerId);
  const experiments = useSelector(selectExperiments);
  const analyticsFacade = useAnalyticsFacade();

  useEffect(() => {
    if (!isExperimentsInitialised && seekerId) {
      // The following will push the new user group to `experiments` state.
      const seekExperimentsAfterAuth = mapToSeekExperiments({
        userId: String(seekerId),
        featureExperiments: FEATURE_EXPERIMENTS_AFTER_AUTH,
        shouldExcludeFromExperiment: false,
        zone,
      });
      if (
        experimentsContainsKey(experiments, genAIOverallCohort) &&
        !experimentsContainsKey(experiments, genAIExperimentInExperimentKey)
      ) {
        experiments[genAIExperimentControlKey as TExperimentKeys] =
          experiments[genAIOverallCohort as TExperimentKeys];
      }

      dispatch(experimentsAfterAuthIntialised(seekExperimentsAfterAuth));

      // Update feature flags after user details are fetched and updated the `experiments` state.
      dispatch(updateFeatureFlagsAfterAuth(cookies, seekExperimentsAfterAuth));

      setIsExperimentsInitialised(true);
    }
    // We don't want to set experiments here again
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cookies, dispatch, isExperimentsInitialised, seekerId, zone]);

  useEffect(() => {
    if (isExperimentsInitialised) {
      analyticsFacade.registerExperiments(experiments);
      if (logger.setFeatureFlags) {
        logger.setFeatureFlags(experiments);
      }
    }
  }, [analyticsFacade, experiments, isExperimentsInitialised]);
};
